import { useMemo } from 'react';
import type { Matcher } from 'react-day-picker';

interface DateBoundsInput {
  startMonth?: Date;
  endMonth?: Date;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Matcher[];
}

/**
 * The months a DatePicker can show and the days it allows. `lowerBound` /
 * `upperBound` are the first and last allowed day (min/max, else the month
 * range), the same for a typed date as for a calendar pick.
 */
export function useDateBounds({
  startMonth,
  endMonth,
  minDate,
  maxDate,
  disabledDates,
}: DateBoundsInput) {
  // ── Effective Month Range ──
  const { effectiveStartMonth, effectiveEndMonth } = useMemo(() => {
    let start = startMonth;
    let end = endMonth;

    if (minDate) {
      const minDateMonth = new Date(
        minDate.getFullYear(),
        minDate.getMonth(),
        1
      );
      if (!start || minDateMonth > start) start = minDateMonth;
    }

    if (maxDate) {
      const maxDateMonth = new Date(
        maxDate.getFullYear(),
        maxDate.getMonth(),
        1
      );
      if (!end || maxDateMonth < end) end = maxDateMonth;
    }

    return { effectiveStartMonth: start, effectiveEndMonth: end };
  }, [startMonth, endMonth, minDate, maxDate]);

  // ── Render Range ──
  const { renderStartMonth, renderEndMonth } = useMemo(() => {
    let renderStart = effectiveStartMonth;
    let renderEnd = effectiveEndMonth;

    if (effectiveStartMonth) {
      renderStart = new Date(effectiveStartMonth);
      renderStart.setMonth(renderStart.getMonth() - 1);
    }

    if (effectiveEndMonth) {
      renderEnd = new Date(effectiveEndMonth);
      renderEnd.setMonth(renderEnd.getMonth() + 1);
    }

    return { renderStartMonth: renderStart, renderEndMonth: renderEnd };
  }, [effectiveStartMonth, effectiveEndMonth]);

  // The first and last day the field allows, typed or picked.
  const { lowerBound, upperBound } = useMemo(
    () => ({
      lowerBound:
        minDate ??
        (effectiveStartMonth
          ? new Date(
              effectiveStartMonth.getFullYear(),
              effectiveStartMonth.getMonth(),
              1
            )
          : undefined),
      upperBound:
        maxDate ??
        (effectiveEndMonth
          ? new Date(
              effectiveEndMonth.getFullYear(),
              effectiveEndMonth.getMonth() + 1,
              0
            )
          : undefined),
    }),
    [minDate, maxDate, effectiveStartMonth, effectiveEndMonth]
  );

  // ── Disabled Dates ──
  const computedDisabledDates: Matcher[] = useMemo(() => {
    const matchers: Matcher[] = [...(disabledDates ?? [])];
    if (lowerBound) matchers.push({ before: lowerBound });
    if (upperBound) matchers.push({ after: upperBound });
    return matchers;
  }, [disabledDates, lowerBound, upperBound]);

  return {
    effectiveStartMonth,
    effectiveEndMonth,
    renderStartMonth,
    renderEndMonth,
    lowerBound,
    upperBound,
    computedDisabledDates,
  };
}
